import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import {
  Container,
  Tabs,
  Card,
  Stack,
  Text,
  Title,
  Grid,
  TextInput,
  NumberInput,
  Select,
  Textarea,
  Button,
  Group,
  Divider,
  Alert,
  MultiSelect
} from '@mantine/core';
import {
  IconUser,
  IconPhoto,
  IconInfoCircle,
  IconDeviceFloppy,
  IconExclamationCircle,
  IconCrown
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import ImageUploader from '../components/profile/ImageUploader';
import { api } from '../services/api';

const ProfilePage = () => {
  const { isAuthenticated, user } = useSelector(state => state.auth);
  
  // Profile state
  const [profileImages, setProfileImages] = useState([]);
  const [leftFootImages, setLeftFootImages] = useState([]);
  const [rightFootImages, setRightFootImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Personal Information
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    age: '',
    weight: '',
    height: '',
    race: '',
    numberOfToes: 10,
    bio: ''
  });

  // Location & Demographics
  const [demographics, setDemographics] = useState({
    country: '',
    state: '',
    city: '',
    zipCode: '',
    occupation: '',
    workoutFrequency: '',
    hobbies: [],
    interests: [],
    education: '',
    relationshipStatus: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      loadUserProfile();
      loadSubscription();
    }
  }, [isAuthenticated]);

  const loadSubscription = async () => {
    try {
      const response = await api.getSubscription();
      if (response.plan) {
        setSubscription(response);
      }
    } catch (err) {
      console.error('Error loading subscription:', err);
      // User might not have a subscription, which is okay
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await api.getUserProfile();
      if (response.profile) {
        // Load existing profile data
        const profile = response.profile;
        setPersonalInfo({
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          age: profile.age || '',
          weight: profile.weight || '',
          height: profile.height || '',
          race: profile.race || '',
          numberOfToes: profile.number_of_toes || 10,
          bio: profile.bio || ''
        });
        
        setDemographics({
          country: profile.country || '',
          state: profile.state || '',
          city: profile.city || '',
          zipCode: profile.zip_code || '',
          occupation: profile.occupation || '',
          workoutFrequency: profile.workout_frequency || '',
          hobbies: profile.hobbies || [],
          interests: profile.interests || [],
          education: profile.education || '',
          relationshipStatus: profile.relationship_status || ''
        });

        // Load existing images - format them for the ImageUploader component
        if (profile.profile_images && profile.profile_images.length > 0) {
          setProfileImages(profile.profile_images.map(img => ({
            id: img.id,
            preview: img.image, // Use the image URL from server
            uploaded: true,
            fromServer: true,
            is_primary: img.is_primary || false
          })));
        }
        if (profile.left_foot_images && profile.left_foot_images.length > 0) {
          setLeftFootImages(profile.left_foot_images.map(img => ({
            id: img.id,
            preview: img.image, // Use the image URL from server
            uploaded: true,
            fromServer: true,
            is_primary: img.is_primary || false
          })));
        }
        if (profile.right_foot_images && profile.right_foot_images.length > 0) {
          setRightFootImages(profile.right_foot_images.map(img => ({
            id: img.id,
            preview: img.image, // Use the image URL from server
            uploaded: true,
            fromServer: true,
            is_primary: img.is_primary || false
          })));
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      // Profile might not exist yet, which is okay for new users
    }
  };

  // Remove this check - we'll wrap the whole component in ProtectedRoute instead

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDemographicsChange = (field, value) => {
    setDemographics(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSetPrimaryImage = async (imageId, imageType) => {
    try {
      const response = await api.setProfileImageAsPrimary(imageId);
      if (response) {
        // Update the local state to reflect the change
        if (imageType === 'profile') {
          setProfileImages(prev => prev.map(img => ({
            ...img,
            is_primary: img.id === imageId
          })));
        } else if (imageType === 'left_foot') {
          setLeftFootImages(prev => prev.map(img => ({
            ...img,
            is_primary: img.id === imageId
          })));
        } else if (imageType === 'right_foot') {
          setRightFootImages(prev => prev.map(img => ({
            ...img,
            is_primary: img.id === imageId
          })));
        }
        notifications.show({
          title: 'Success',
          message: 'Primary image updated successfully',
          color: 'green'
        });
      }
    } catch (err) {
      console.error('Error setting primary image:', err);
      notifications.show({
        title: 'Error',
        message: 'Failed to update primary image',
        color: 'red'
      });
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError(null);
    
    try {
      // Prepare profile data for API
      const profileData = {
        first_name: personalInfo.firstName,
        last_name: personalInfo.lastName,
        age: personalInfo.age || null,  // Convert empty string to null
        weight: personalInfo.weight || null,  // Convert empty string to null
        height: personalInfo.height,
        race: personalInfo.race,
        number_of_toes: personalInfo.numberOfToes || 10,  // Default to 10 if empty
        bio: personalInfo.bio,
        country: demographics.country,
        state: demographics.state,
        city: demographics.city,
        zip_code: demographics.zipCode,
        occupation: demographics.occupation,
        workout_frequency: demographics.workoutFrequency,
        hobbies: demographics.hobbies,
        interests: demographics.interests,
        education: demographics.education,
        relationship_status: demographics.relationshipStatus
      };
      
      console.log('Saving profile:', profileData);
      
      // Save basic profile data
      await api.updateUserProfile(profileData);
      
      // Upload images
      const uploadPromises = [];
      
      // Upload profile images
      for (const img of profileImages) {
        if (img.file && !img.uploaded) {
          uploadPromises.push(
            api.uploadProfileImage(img.file, 'profile')
              .then(response => {
                console.log('Profile image uploaded:', response);
                img.uploaded = true;
                img.id = response.image?.id;
              })
              .catch(err => console.error('Failed to upload profile image:', err))
          );
        }
      }
      
      // Upload left foot images
      for (const img of leftFootImages) {
        if (img.file && !img.uploaded) {
          uploadPromises.push(
            api.uploadProfileImage(img.file, 'left_foot')
              .then(response => {
                console.log('Left foot image uploaded:', response);
                img.uploaded = true;
                img.id = response.image?.id;
              })
              .catch(err => console.error('Failed to upload left foot image:', err))
          );
        }
      }
      
      // Upload right foot images
      for (const img of rightFootImages) {
        if (img.file && !img.uploaded) {
          uploadPromises.push(
            api.uploadProfileImage(img.file, 'right_foot')
              .then(response => {
                console.log('Right foot image uploaded:', response);
                img.uploaded = true;
                img.id = response.image?.id;
              })
              .catch(err => console.error('Failed to upload right foot image:', err))
          );
        }
      }
      
      // Wait for all uploads to complete
      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
        console.log('All images uploaded successfully');
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.response?.data?.error || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Options for select fields
  const raceOptions = [
    { value: 'asian', label: 'Asian' },
    { value: 'black', label: 'Black or African American' },
    { value: 'hispanic', label: 'Hispanic or Latino' },
    { value: 'native', label: 'Native American' },
    { value: 'pacific', label: 'Pacific Islander' },
    { value: 'white', label: 'White' },
    { value: 'mixed', label: 'Mixed Race' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const workoutFrequencyOptions = [
    { value: 'never', label: 'Never' },
    { value: 'rarely', label: 'Rarely (less than once a month)' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' },
    { value: '2-3_times_week', label: '2-3 times per week' },
    { value: '4-5_times_week', label: '4-5 times per week' },
    { value: 'daily', label: 'Daily' },
    { value: 'multiple_daily', label: 'Multiple times daily' }
  ];

  const hobbyOptions = [
    { value: 'reading', label: 'Reading' },
    { value: 'cooking', label: 'Cooking' },
    { value: 'gardening', label: 'Gardening' },
    { value: 'photography', label: 'Photography' },
    { value: 'traveling', label: 'Traveling' },
    { value: 'music', label: 'Music' },
    { value: 'sports', label: 'Sports' },
    { value: 'gaming', label: 'Gaming' },
    { value: 'art', label: 'Art & Crafts' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'dancing', label: 'Dancing' },
    { value: 'writing', label: 'Writing' },
    { value: 'hiking', label: 'Hiking' },
    { value: 'yoga', label: 'Yoga' },
    { value: 'meditation', label: 'Meditation' },
    { value: 'movies', label: 'Movies & TV' },
    { value: 'fashion', label: 'Fashion' },
    { value: 'technology', label: 'Technology' }
  ];

  const relationshipOptions = [
    { value: 'single', label: 'Single' },
    { value: 'dating', label: 'Dating' },
    { value: 'relationship', label: 'In a relationship' },
    { value: 'engaged', label: 'Engaged' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' },
    { value: 'complicated', label: 'It\'s complicated' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const educationOptions = [
    { value: 'high_school', label: 'High School' },
    { value: 'some_college', label: 'Some College' },
    { value: 'associates', label: 'Associate\'s Degree' },
    { value: 'bachelors', label: 'Bachelor\'s Degree' },
    { value: 'masters', label: 'Master\'s Degree' },
    { value: 'doctorate', label: 'Doctorate' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  return (
    <ProtectedRoute requireAuth={true} redirectTo="/">
      <Container size="lg" py="xl">
        <Stack gap="xl">
        <div>
          <Title order={1}>My Profile</Title>
          <Text c="dimmed" mt="xs">
            Manage your personal information and photos
          </Text>
        </div>

        {saveSuccess && (
          <Alert color="green" title="Profile Saved!">
            Your profile has been updated successfully.
          </Alert>
        )}

        {error && (
          <Alert 
            icon={<IconExclamationCircle size="1rem" />} 
            color="red" 
            title="Error"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        <Tabs defaultValue="photos" variant="outline">
          <Tabs.List>
            <Tabs.Tab value="photos" leftSection={<IconPhoto size="0.8rem" />}>
              Photos
            </Tabs.Tab>
            <Tabs.Tab value="personal" leftSection={<IconUser size="0.8rem" />}>
              Personal Info
            </Tabs.Tab>
            <Tabs.Tab value="demographics" leftSection={<IconInfoCircle size="0.8rem" />}>
              Demographics
            </Tabs.Tab>
            <Tabs.Tab value="subscription" leftSection={<IconCrown size="0.8rem" />}>
              Subscription
            </Tabs.Tab>
          </Tabs.List>

          {/* Photos Tab */}
          <Tabs.Panel value="photos" pt="lg">
            <Stack gap="xl">
              <ImageUploader
                title="Profile Pictures"
                description="Upload up to 10 profile pictures that represent you"
                images={profileImages}
                onImagesChange={setProfileImages}
                maxImages={10}
                allowPrimarySelection={true}
                onSetPrimary={(imageId) => handleSetPrimaryImage(imageId, 'profile')}
              />

              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <ImageUploader
                    title="Left Foot Photos"
                    description="Upload up to 10 photos of your left foot"
                    images={leftFootImages}
                    onImagesChange={setLeftFootImages}
                    maxImages={10}
                  />
                </Grid.Col>
                
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <ImageUploader
                    title="Right Foot Photos"
                    description="Upload up to 10 photos of your right foot"
                    images={rightFootImages}
                    onImagesChange={setRightFootImages}
                    maxImages={10}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Tabs.Panel>

          {/* Personal Information Tab */}
          <Tabs.Panel value="personal" pt="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3}>Personal Information</Title>
                <Text size="sm" c="dimmed">
                  All fields are optional. Share as much or as little as you're comfortable with.
                </Text>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="First Name"
                      placeholder="Enter your first name"
                      value={personalInfo.firstName}
                      onChange={(e) => handlePersonalInfoChange('firstName', e.currentTarget.value)}
                    />
                  </Grid.Col>
                  
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Last Name"
                      placeholder="Enter your last name"
                      value={personalInfo.lastName}
                      onChange={(e) => handlePersonalInfoChange('lastName', e.currentTarget.value)}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <NumberInput
                      label="Age"
                      placeholder="Your age"
                      min={13}
                      max={120}
                      value={personalInfo.age}
                      onChange={(value) => handlePersonalInfoChange('age', value)}
                    />
                  </Grid.Col>
                  
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <NumberInput
                      label="Weight (lbs)"
                      placeholder="Your weight"
                      min={50}
                      max={1000}
                      value={personalInfo.weight}
                      onChange={(value) => handlePersonalInfoChange('weight', value)}
                    />
                  </Grid.Col>
                  
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="Height"
                      placeholder="e.g., 5'6'', 170cm"
                      value={personalInfo.height}
                      onChange={(e) => handlePersonalInfoChange('height', e.currentTarget.value)}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Race/Ethnicity"
                      placeholder="Select your race/ethnicity"
                      data={raceOptions}
                      value={personalInfo.race}
                      onChange={(value) => handlePersonalInfoChange('race', value)}
                      clearable
                    />
                  </Grid.Col>
                  
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <NumberInput
                      label="Number of Toes"
                      placeholder="Total number of toes"
                      min={0}
                      max={20}
                      value={personalInfo.numberOfToes}
                      onChange={(value) => handlePersonalInfoChange('numberOfToes', value)}
                    />
                  </Grid.Col>
                </Grid>

                <Textarea
                  label="Bio"
                  placeholder="Tell us about yourself..."
                  rows={4}
                  value={personalInfo.bio}
                  onChange={(e) => handlePersonalInfoChange('bio', e.currentTarget.value)}
                />
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* Subscription Tab */}
          <Tabs.Panel value="subscription" pt="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3}>Subscription Status</Title>
                {subscription && subscription.is_active ? (
                  <>
                    <Alert color="green" variant="light">
                      <Group gap="sm">
                        <IconCrown size="1.2rem" />
                        <div>
                          <Text fw={500}>Active Subscription</Text>
                          <Text size="sm" c="dimmed">
                            {subscription.plan_name} - ${subscription.price}/month
                          </Text>
                        </div>
                      </Group>
                    </Alert>
                    <Text size="sm">
                      <strong>Started:</strong> {new Date(subscription.start_date).toLocaleDateString()}
                    </Text>
                    <Text size="sm">
                      <strong>Current Period Ends:</strong> {new Date(subscription.current_period_end).toLocaleDateString()}
                    </Text>
                    <Text size="sm">
                      <strong>Status:</strong> {subscription.status}
                    </Text>
                    <Divider my="md" />
                    <Button 
                      component="a" 
                      href="/subscription" 
                      variant="light"
                      leftSection={<IconCrown size="1rem" />}
                    >
                      Manage Subscription
                    </Button>
                  </>
                ) : (
                  <>
                    <Alert color="gray" variant="light">
                      <Text>You don't have an active subscription</Text>
                    </Alert>
                    <Text size="sm" c="dimmed">
                      Subscribe to unlock premium features and support your favorite Princesses!
                    </Text>
                    <Button 
                      component="a" 
                      href="/subscription" 
                      variant="filled"
                      leftSection={<IconCrown size="1rem" />}
                    >
                      View Subscription Plans
                    </Button>
                  </>
                )}
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* Demographics Tab */}
          <Tabs.Panel value="demographics" pt="lg">
            <Stack gap="lg">
              {/* Location */}
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Title order={4}>Location</Title>
                  
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Country"
                        placeholder="Your country"
                        value={demographics.country}
                        onChange={(e) => handleDemographicsChange('country', e.currentTarget.value)}
                      />
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="State/Province"
                        placeholder="Your state or province"
                        value={demographics.state}
                        onChange={(e) => handleDemographicsChange('state', e.currentTarget.value)}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 8 }}>
                      <TextInput
                        label="City"
                        placeholder="Your city"
                        value={demographics.city}
                        onChange={(e) => handleDemographicsChange('city', e.currentTarget.value)}
                      />
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, md: 4 }}>
                      <TextInput
                        label="ZIP/Postal Code"
                        placeholder="ZIP code"
                        value={demographics.zipCode}
                        onChange={(e) => handleDemographicsChange('zipCode', e.currentTarget.value)}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Card>

              {/* Lifestyle */}
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Title order={4}>Lifestyle & Interests</Title>
                  
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Occupation"
                        placeholder="What do you do for work?"
                        value={demographics.occupation}
                        onChange={(e) => handleDemographicsChange('occupation', e.currentTarget.value)}
                      />
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Workout Frequency"
                        placeholder="How often do you work out?"
                        data={workoutFrequencyOptions}
                        value={demographics.workoutFrequency}
                        onChange={(value) => handleDemographicsChange('workoutFrequency', value)}
                        clearable
                      />
                    </Grid.Col>
                  </Grid>

                  <MultiSelect
                    label="Hobbies"
                    placeholder="Select your hobbies"
                    data={hobbyOptions}
                    value={demographics.hobbies}
                    onChange={(value) => handleDemographicsChange('hobbies', value)}
                    searchable
                  />

                  <MultiSelect
                    label="Interests"
                    placeholder="What else are you interested in? (Type to add custom interests)"
                    data={hobbyOptions}
                    value={demographics.interests}
                    onChange={(value) => handleDemographicsChange('interests', value)}
                    searchable
                    creatable
                    getCreateLabel={(query) => `+ Add "${query}"`}
                  />

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Education Level"
                        placeholder="Highest education completed"
                        data={educationOptions}
                        value={demographics.education}
                        onChange={(value) => handleDemographicsChange('education', value)}
                        clearable
                      />
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Relationship Status"
                        placeholder="Your relationship status"
                        data={relationshipOptions}
                        value={demographics.relationshipStatus}
                        onChange={(value) => handleDemographicsChange('relationshipStatus', value)}
                        clearable
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {/* Save Button */}
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group justify="center">
            <Button
              leftSection={<IconDeviceFloppy size="1rem" />}
              size="lg"
              onClick={handleSaveProfile}
              loading={saving}
            >
              Save Profile
            </Button>
          </Group>
        </Card>
      </Stack>
    </Container>
    </ProtectedRoute>
  );
};

export default ProfilePage;